// =============================================================================
// FILE: components/hover/HoverPalette.tsx
// PURPOSE: R2 Smart Hover Intelligence — floating contextual action palette
//   Appears near hovered elements with type-specific actions.
//   Dark glass, animated entrance, auto-adjusts to viewport edges.
// =============================================================================

import { type JSX } from 'react';
import {
  Sparkles, HelpCircle, GitBranch, Link2, Copy, PlusCircle, Bookmark, FolderOpen,
  AlignLeft, Reply, ListChecks, CalendarPlus, Paperclip, Link,
  CheckSquare, List, FolderCheck, Clock, Brain, GitFork,
  ZoomIn, Layers, Package, FileText, Zap, AlertTriangle,
  PenLine, Languages, Users, Activity, Wrench, MapPin,
  User, Mail, X, ArrowRight,
} from 'lucide-react';
import type { HoverElementType, HoverAction } from '../../stores/hoverIntelligence.store.js';
import { HOVER_ACTIONS, ELEMENT_TYPE_LABELS } from '../../stores/hoverIntelligence.store.js';

// Icon registry
const ICON_MAP: Record<string, JSX.Element> = {
  Sparkles:     <Sparkles size={13} />,
  HelpCircle:   <HelpCircle size={13} />,
  GitBranch:    <GitBranch size={13} />,
  Link2:        <Link2 size={13} />,
  Copy:         <Copy size={13} />,
  PlusCircle:   <PlusCircle size={13} />,
  Bookmark:     <Bookmark size={13} />,
  FolderOpen:   <FolderOpen size={13} />,
  AlignLeft:    <AlignLeft size={13} />,
  Reply:        <Reply size={13} />,
  ListChecks:   <ListChecks size={13} />,
  CalendarPlus: <CalendarPlus size={13} />,
  Paperclip:    <Paperclip size={13} />,
  Link:         <Link size={13} />,
  CheckSquare:  <CheckSquare size={13} />,
  List:         <List size={13} />,
  FolderCheck:  <FolderCheck size={13} />,
  Clock:        <Clock size={13} />,
  Brain:        <Brain size={13} />,
  GitFork:      <GitFork size={13} />,
  ZoomIn:       <ZoomIn size={13} />,
  Layers:       <Layers size={13} />,
  Package:      <Package size={13} />,
  FileText:     <FileText size={13} />,
  FileStack:    <Bookmark size={13} />,
  Zap:          <Zap size={13} />,
  AlertTriangle: <AlertTriangle size={13} />,
  PenLine:      <PenLine size={13} />,
  Languages:    <Languages size={13} />,
  Users:        <Users size={13} />,
  Activity:     <Activity size={13} />,
  Wrench:       <Wrench size={13} />,
  MapPin:       <MapPin size={13} />,
  User:         <User size={13} />,
  Mail:         <Mail size={13} />,
};

const TYPE_ICONS: Record<HoverElementType, JSX.Element> = {
  document: <FileText size={12} />,
  email:    <Mail size={12} />,
  event:    <Clock size={12} />,
  drawing:  <Layers size={12} />,
  code:     <GitBranch size={12} />,
  text:     <AlignLeft size={12} />,
  project:  <FolderOpen size={12} />,
  asset:    <Package size={12} />,
  contact:  <User size={12} />,
};

interface HoverPaletteProps {
  elementType: HoverElementType;
  context?: { title?: string; description?: string };
  position: { x: number; y: number };
  maxActions: number;
  showAIBadge: boolean;
  onAction: (actionId: string, type: HoverElementType) => void;
  onClose: () => void;
}

export function HoverPalette({
  elementType, context, position, maxActions, showAIBadge, onAction, onClose,
}: HoverPaletteProps): JSX.Element {
  const actions = (HOVER_ACTIONS[elementType] ?? []).slice(0, maxActions);
  const label = ELEMENT_TYPE_LABELS[elementType] ?? elementType;

  // Auto-flip so palette stays within viewport
  const vpW = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vpH = typeof window !== 'undefined' ? window.innerHeight : 800;
  const PALETTE_W = 230;
  const PALETTE_H = 58 + actions.length * 36; // approx
  const flipX = position.x + 16 + PALETTE_W > vpW;
  const flipY = position.y + PALETTE_H > vpH;

  const left = flipX ? position.x - PALETTE_W - 8 : position.x + 16;
  const top  = flipY ? position.y - PALETTE_H - 4 : position.y + 8;

  return (
    <div
      data-testid="hover-palette"
      onMouseEnter={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        left,
        top,
        zIndex: 99900,
        width: PALETTE_W,
        background: 'rgba(3,8,22,0.97)',
        border: '1px solid rgba(37,181,168,0.22)',
        borderRadius: 12,
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        boxShadow: '0 8px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(37,181,168,0.06)',
        animation: 'hoverPaletteIn 0.18s cubic-bezier(0.16,1,0.3,1)',
        overflow: 'hidden',
        pointerEvents: 'auto',
      }}
    >
      {/* Accent bar */}
      <div style={{ height: 2, background: 'linear-gradient(90deg, #25B5A8, #60a5fa, transparent)' }} />

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 7,
        padding: '8px 12px 6px',
        borderBottom: '1px solid rgba(37,181,168,0.1)',
      }}>
        <span style={{ color: '#5eead4', opacity: 0.8 }}>
          {TYPE_ICONS[elementType]}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.14em',
            color: 'rgba(37,181,168,0.6)', fontWeight: 700, fontFamily: 'monospace',
          }}>
            R2 · {label}
          </div>
          {context?.title && (
            <div style={{
              fontSize: 11, color: '#dce8f8', fontWeight: 600,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              marginTop: 1,
            }}>
              {context.title}
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'rgba(100,140,180,0.35)', padding: 2, borderRadius: 4,
            flexShrink: 0,
          }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,172,210,0.7)')}
          onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(100,140,180,0.35)')}
        >
          <X size={11} />
        </button>
      </div>

      {/* Actions */}
      <div style={{ padding: '4px 6px 8px' }}>
        {actions.map((action) => (
          <ActionRow
            key={action.id}
            action={action}
            showAIBadge={showAIBadge}
            onClick={() => onAction(action.id, elementType)}
          />
        ))}
      </div>

      <style>{`
        @keyframes hoverPaletteIn {
          from { opacity: 0; transform: translateY(-6px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
      `}</style>
    </div>
  );
}

function ActionRow({
  action, showAIBadge, onClick,
}: {
  action: HoverAction;
  showAIBadge: boolean;
  onClick: () => void;
}): JSX.Element {
  const icon = ICON_MAP[action.icon] ?? <ArrowRight size={13} />;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`hover-action-${action.id}`}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 8px', borderRadius: 7, cursor: 'pointer',
        background: 'none', border: 'none', textAlign: 'left',
        color: '#dce8f8', transition: 'background 0.1s',
      }}
      onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.background = 'rgba(37,181,168,0.07)')}
      onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.background = 'none')}
    >
      {/* Icon */}
      <span style={{
        width: 24, height: 24, borderRadius: 6, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `${action.color}14`,
        border: `1px solid ${action.color}28`,
        color: action.color,
      }}>
        {icon}
      </span>

      {/* Label */}
      <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: '#c8d8f0' }}>
        {action.label}
      </span>

      {/* AI badge */}
      {showAIBadge && action.requiresAI && (
        <span style={{
          fontSize: 8, padding: '1px 5px', borderRadius: 4,
          background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.25)',
          color: '#a78bfa', fontWeight: 700, letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}>
          AI
        </span>
      )}

      {/* Shortcut */}
      {action.shortcut && (
        <span style={{
          fontSize: 9, padding: '1px 4px', borderRadius: 3,
          background: 'rgba(30,50,80,0.6)', border: '1px solid rgba(50,70,100,0.5)',
          color: 'rgba(148,172,210,0.45)', fontFamily: 'monospace',
        }}>
          {action.shortcut}
        </span>
      )}
    </button>
  );
}
